'use client';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

export default function TableQuestionEdit({
  question,
  setQuestion,
  errorMessage,
  helperText,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const tableOptions = question.tableSetting?.options || [];

  const handleColumnChange = (index, field, value) => {
    const updatedOptions = [...tableOptions];

    if (field === 'value') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        value: value,
      };
    } else if (field === 'type') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        type: value,
      };
    } else if (field === 'required') {
      updatedOptions[index] = {
        ...updatedOptions[index],
        required: value,
      };
    }

    setQuestion({
      ...question,
      tableSetting: {
        ...question.tableSetting,
        options: updatedOptions,
      },
    });
  };

  const handleAddColumn = () => {
    if (tableOptions.length >= 15) {
      enqueueSnackbar('Máximo 15 columnas permitidas', {
        variant: 'warning',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    setQuestion({
      ...question,
      tableSetting: {
        ...question.tableSetting,
        options: [
          ...tableOptions,
          {
            required: false,
            type: 'text',
            value: '',
          },
        ],
      },
    });
  };

  const handleRemoveColumn = (index) => {
    if (tableOptions.length <= 1) {
      enqueueSnackbar('Debe haber al menos una columna', {
        variant: 'warning',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    const updatedOptions = [...tableOptions];
    updatedOptions.splice(index, 1);

    setQuestion({
      ...question,
      tableSetting: {
        ...question.tableSetting,
        options: updatedOptions,
      },
    });
  };

  const getColumnValue = (option) => {
    return option?.value || '';
  };

  const getColumnType = (option) => {
    return option?.type || 'text';
  };

  const getColumnRequired = (option) => {
    return option?.required || false;
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {errorMessage?.tableSetting && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {helperText?.tableSetting || 'Error en configuración de tabla'}
        </Alert>
      )}

      <Card elevation={3} sx={{ borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {/* Información de la tabla */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#e8f0fe' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TableChartIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} color="#333">
                    Configuración de Tabla
                  </Typography>
                  <Typography variant="body2" color="#555">
                    Los usuarios podrán agregar tantas filas como necesiten. Solo
                    define las columnas.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Configuración de columnas */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: errorMessage?.columns ? '2px solid #d32f2f' : 'none',
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Columnas de la tabla
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Mínimo 1, máximo 15 columnas
              </Typography>

              <Stack spacing={2}>
                {tableOptions.map((option, index) => (
                  <Paper
                    key={index}
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      bgcolor: '#fafafa',
                    }}
                  >
                    {/* Encabezado de columna */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: '40px',
                          height: '40px',
                          bgcolor: 'primary.light',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <TextField
                        placeholder={`Nombre de columna ${index + 1}`}
                        value={getColumnValue(option)}
                        onChange={(e) =>
                          handleColumnChange(index, 'value', e.target.value)
                        }
                        fullWidth
                        size="small"
                        error={errorMessage?.columns?.[index]}
                        helperText={
                          errorMessage?.columns?.[index]
                            ? 'La columna no puede estar vacía'
                            : ''
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'white',
                            borderRadius: 1.5,
                          },
                        }}
                      />
                      {tableOptions.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveColumn(index)}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': {
                              bgcolor: 'rgba(211, 47, 47, 0.08)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Configuración de validación */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                        <InputLabel>Tipo de validación</InputLabel>
                        <Select
                          value={getColumnType(option)}
                          label="Tipo de validación"
                          onChange={(e) =>
                            handleColumnChange(index, 'type', e.target.value)
                          }
                          sx={{
                            bgcolor: 'white',
                            borderRadius: 1.5,
                          }}
                        >
                          <MenuItem value="text">
                            📝 Alfanumérico (texto libre)
                          </MenuItem>
                          <MenuItem value="number">🔢 Solo números</MenuItem>
                          <MenuItem value="email">
                            📧 Solo correo electrónico
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={getColumnRequired(option)}
                            onChange={(e) =>
                              handleColumnChange(
                                index,
                                'required',
                                e.target.checked
                              )
                            }
                            size="small"
                          />
                        }
                        label="Campo requerido"
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Paper>
                ))}
              </Stack>

              {/* Botón agregar columna */}
              <Box sx={{ mt: 3 }}>
                {tableOptions.length < 15 && (
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddColumn}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderStyle: 'dashed',
                      '&:hover': {
                        borderStyle: 'dashed',
                        bgcolor: 'primary.50',
                      },
                    }}
                  >
                    Añadir columna
                  </Button>
                )}
              </Box>
            </Paper>

            {/* Resumen de configuración */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Configuración actual:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • Total de columnas: <strong>{tableOptions.length}</strong>
              </Typography>
              <Typography variant="body2">
                • Columnas requeridas:{' '}
                <strong>
                  {tableOptions.filter((opt) => opt.required).length}
                </strong>
              </Typography>
              <Typography variant="body2">
                • Los usuarios podrán agregar{' '}
                <strong>filas ilimitadas</strong> al responder
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}